import React from "react";
import Navbar from "./Navbar";
import AddFolderButton from "./AddFolderButton";
import useFolder from "../../hooks/useFolder";
import Folder from "./Folder";
import FolderBreadCrumbs from "./FolderBreadCrumbs";
import AddFileButton from "./AddFileButton";
import File from "./File";
import { FolderIcon, FileIcon, PlusIcon } from "lucide-react";

const Dashboard = () => {
  const fullUrl = window.location.href;
  const urlPath = new URL(fullUrl).pathname;
  const folderPath = decodeURIComponent(urlPath.split("/docvault/")[1] || "");

  const role = localStorage.getItem("type");
  const currentUserId = localStorage.getItem("userID");

  const [searchUserId, setSearchUserId] = React.useState(currentUserId);
  const [inputUserId, setInputUserId] = React.useState(currentUserId);
  const [refreshTrigger, setRefreshTrigger] = React.useState(0);
  const forceRefresh = () => setRefreshTrigger((prev) => prev + 1);

  const { folder, childFolders, childFiles, loading } = useFolder(folderPath, searchUserId, refreshTrigger);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="flex items-center space-x-4 bg-white p-6 rounded-xl shadow-lg">
          <div className="w-4 h-4 bg-blue-500 rounded-full animate-pulse"></div>
          <div className="w-4 h-4 bg-blue-500 rounded-full animate-pulse delay-100"></div>
          <div className="w-4 h-4 bg-blue-500 rounded-full animate-pulse delay-200"></div>
          <span className="text-gray-600 ml-2">Loading your documents...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <main className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Folder Navigation and Actions */}
        <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 p-5 mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex flex-col w-full">
              {/* Admin Search Input */}
              {role === "admin" && (
                <div className="mb-2 flex justify-left items-center">
                  <label className="block text-gray-700 font-medium mb-2">
                    Search by User ID:
                  </label>
                  <div className="flex space-x-2 w-full  ml-4 sm:w-1/2 lg:w-1/3">
                    <input
                      type="text"
                      value={inputUserId}
                      onChange={(e) => setInputUserId(e.target.value)}
                      placeholder="Enter user ID"
                      className="border border-gray-300 rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                    <button
                      onClick={() => setSearchUserId(inputUserId)}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-all"
                    >
                      Search
                    </button>
                  </div>
                </div>
              )}

              {/* Breadcrumbs */}
              <FolderBreadCrumbs currentFolder={folder} path={folderPath} />
            </div>

            {/* Action Buttons */}
            {role !== "student" && (
              <div className="flex items-center space-x-4">
                <AddFileButton
                  userID={searchUserId} onSuccess={forceRefresh}
                  className="transition-all duration-300 hover:scale-105 active:scale-95"
                />
                <AddFolderButton className="transition-all duration-300 hover:scale-105 active:scale-95" userID={searchUserId} onSuccess={forceRefresh}/>
              </div>
            )}
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          {/* Folders Section */}
          {childFolders?.length > 0 && (
            <section className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                <FolderIcon className="w-6 h-6 mr-3 text-blue-600" />
                Folders
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {childFolders.map((childFolder) => (
                  <div key={childFolder.path} className="group">
                    <Folder folder={childFolder} parentPath={folderPath} userID={searchUserId} onSuccess={forceRefresh} />
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Files Section */}
          {childFiles?.length > 0 && (
            <section className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                <FileIcon className="w-6 h-6 mr-3 text-green-600" />
                Files
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {childFiles.map((file) => (
                  <div key={file.filePath} className="group">
                    <File file={file} parentPath={folderPath} userID={searchUserId} onSuccess={forceRefresh} />
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Empty State */}
          {!childFolders?.length && !childFiles?.length && (
            <div className="text-center py-12">
              <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 p-10 max-w-md mx-auto">
                <div className="mb-6 flex justify-center items-center w-24 h-24 mx-auto bg-blue-50 rounded-full">
                  <PlusIcon className="w-12 h-12 text-blue-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  No items found
                </h3>
                <p className="text-gray-600 mb-6">
                  Start by creating a new folder or uploading files.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
